import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, User, Settings, Users, FileText, Mail } from 'lucide-react';
import { UserWithDetails, UserPermissions } from '@/hooks/useUsers';

interface UsuarioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usuario: UserWithDetails | null;
  onSave: (data: {
    profileId: string;
    fullName: string;
    role: 'admin' | 'manager' | 'operator';
    permissions: Partial<UserPermissions>;
  }) => void;
  onInvite?: (data: {
    email: string;
    fullName: string;
    role: 'admin' | 'manager' | 'operator';
    permissions: Partial<UserPermissions>;
  }) => Promise<void>;
  isSaving?: boolean;
}

const PERMISSION_GROUPS = {
  'Clientes': {
    icon: Users,
    permissions: [
      { key: 'criar_cliente', label: 'Criar/Importar' },
      { key: 'alterar_cliente', label: 'Alterar' },
      { key: 'apagar_cliente', label: 'Apagar' },
    ]
  },
  'Réguas de Cobrança': {
    icon: FileText,
    permissions: [
      { key: 'criar_regua', label: 'Criar' },
      { key: 'alterar_regua', label: 'Alterar' },
      { key: 'apagar_regua', label: 'Apagar' },
    ]
  },
  'Usuários': {
    icon: User,
    permissions: [
      { key: 'listar_usuario', label: 'Ver Lista' },
      { key: 'alterar_usuario', label: 'Criar/Alterar' },
      { key: 'apagar_usuario', label: 'Apagar' },
    ]
  },
  'Configurações': {
    icon: Settings,
    permissions: [
      { key: 'alterar_configuracoes', label: 'Editar Configurações' },
      { key: 'ver_config_banco', label: 'Visualizar Banco' },
      { key: 'ver_config_empresa', label: 'Visualizar Empresa' },
      { key: 'ver_config_email', label: 'Visualizar E-mail' },
      { key: 'ver_config_whatsapp', label: 'Visualizar WhatsApp' },
    ]
  }
} as const;

type PermissionKey = keyof Omit<UserPermissions, 'id' | 'user_id' | 'company_id'>;

const ROLE_LABELS: Record<string, { label: string; description: string; color: string }> = {
  admin: { 
    label: 'Administrador', 
    description: 'Acesso total ao sistema',
    color: 'hsl(var(--accent-500))'
  },
  manager: { 
    label: 'Gerente', 
    description: 'Gerencia equipe e operações',
    color: 'hsl(var(--basic-500))'
  },
  operator: { 
    label: 'Operador', 
    description: 'Acesso limitado conforme permissões',
    color: 'hsl(var(--muted-foreground))'
  },
};

export function UsuarioModal({ 
  open, 
  onOpenChange, 
  usuario,
  onSave,
  onInvite,
  isSaving 
}: UsuarioModalProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'manager' | 'operator'>('operator');
  const [permissions, setPermissions] = useState<Record<PermissionKey, boolean>>({
    criar_cliente: false,
    alterar_cliente: false,
    apagar_cliente: false,
    criar_regua: false,
    alterar_regua: false,
    apagar_regua: false,
    listar_usuario: false,
    alterar_usuario: false,
    apagar_usuario: false,
    alterar_configuracoes: false,
    ver_config_banco: false,
    ver_config_empresa: false,
    ver_config_email: false,
    ver_config_whatsapp: false,
  });
  const [error, setError] = useState('');

  const isNewUser = !usuario;

  useEffect(() => {
    if (open) {
      if (usuario) {
        setFullName(usuario.full_name || '');
        setEmail('');
        setRole(usuario.role || 'operator');
        
        if (usuario.permissions) {
          setPermissions({
            criar_cliente: usuario.permissions.criar_cliente,
            alterar_cliente: usuario.permissions.alterar_cliente,
            apagar_cliente: usuario.permissions.apagar_cliente,
            criar_regua: usuario.permissions.criar_regua,
            alterar_regua: usuario.permissions.alterar_regua,
            apagar_regua: usuario.permissions.apagar_regua,
            listar_usuario: usuario.permissions.listar_usuario,
            alterar_usuario: usuario.permissions.alterar_usuario,
            apagar_usuario: usuario.permissions.apagar_usuario,
            alterar_configuracoes: usuario.permissions.alterar_configuracoes,
            ver_config_banco: (usuario.permissions as any).ver_config_banco || false,
            ver_config_empresa: (usuario.permissions as any).ver_config_empresa || false,
            ver_config_email: (usuario.permissions as any).ver_config_email || false,
            ver_config_whatsapp: (usuario.permissions as any).ver_config_whatsapp || false,
          });
        } else {
          resetPermissions();
        }
      } else {
        // New user
        setFullName('');
        setEmail('');
        setRole('operator');
        resetPermissions();
      }
      setError('');
    }
  }, [open, usuario]);

  const resetPermissions = () => {
    setPermissions({
      criar_cliente: false,
      alterar_cliente: false,
      apagar_cliente: false,
      criar_regua: false,
      alterar_regua: false,
      apagar_regua: false,
      listar_usuario: false,
      alterar_usuario: false,
      apagar_usuario: false,
      alterar_configuracoes: false,
      ver_config_banco: false,
      ver_config_empresa: false,
      ver_config_email: false,
      ver_config_whatsapp: false,
    });
  };

  const handlePermissionChange = (key: PermissionKey, checked: boolean) => {
    setPermissions(prev => ({ ...prev, [key]: checked }));
  };

  const handleSelectAll = (groupKeys: PermissionKey[]) => {
    const allSelected = groupKeys.every(k => permissions[k]);
    const newValue = !allSelected;
    setPermissions(prev => {
      const updated = { ...prev };
      groupKeys.forEach(k => { updated[k] = newValue; });
      return updated;
    });
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (isNewUser) {
      // Creating new user
      if (!email.trim()) {
        setError('Email é obrigatório');
        return;
      }
      if (!validateEmail(email)) {
        setError('Email inválido');
        return;
      }
      if (!onInvite) {
        setError('Função de convite não disponível');
        return;
      }

      setError('');
      try {
        await onInvite({
          email: email.trim(),
          fullName: fullName.trim(),
          role,
          permissions,
        });
      } catch (err: any) {
        setError(err.message || 'Erro ao convidar usuário');
      }
    } else {
      // Editing existing user
      if (!usuario) {
        setError('Usuário não encontrado');
        return;
      }

      setError('');
      onSave({
        profileId: usuario.id,
        fullName: fullName.trim(),
        role,
        permissions,
      });
    }
  };

  const isAdmin = role === 'admin';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isNewUser ? (
              <>
                <Mail className="h-5 w-5 text-[hsl(var(--accent-500))]" />
                Convidar Novo Usuário
              </>
            ) : (
              <>
                <Shield className="h-5 w-5 text-[hsl(var(--basic-500))]" />
                Editar Usuário
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Info message for new users */}
          {isNewUser && (
            <div className="p-3 rounded-lg bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))] text-sm">
              <p className="font-medium mb-1">📧 Convite por Email</p>
              <p className="text-xs opacity-80">
                O usuário receberá um email com link para definir sua senha e acessar o sistema.
              </p>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Nome Completo *</Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Digite o nome completo"
              />
            </div>
            
            {isNewUser ? (
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="usuario@empresa.com"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="user_id">ID do Usuário</Label>
                <Input
                  id="user_id"
                  value={usuario?.user_id?.slice(0, 8) + '...' || ''}
                  disabled
                  className="bg-muted font-mono text-sm"
                />
              </div>
            )}
          </div>

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>Tipo de Usuário (Alçada)</Label>
            <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione o cargo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([key, info]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: info.color }}
                      />
                      <span className="font-medium">{info.label}</span>
                      <span className="text-muted-foreground text-xs">— {info.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Permissions Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Permissões de Acesso
              </h3>
              {isAdmin && (
                <span className="text-xs bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))] px-2 py-1 rounded-full font-medium">
                  Administradores têm acesso total
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(PERMISSION_GROUPS).map(([groupName, group]) => {
                const groupKeys = group.permissions.map(p => p.key as PermissionKey);
                const allSelected = groupKeys.every(k => permissions[k] || isAdmin);
                const IconComponent = group.icon;

                return (
                  <div key={groupName} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <IconComponent className="h-4 w-4 text-[hsl(var(--basic-400))]" />
                        <span className="text-[11px] font-bold text-[hsl(var(--basic-500))] uppercase tracking-tight">
                          {groupName}
                        </span>
                      </div>
                      {!isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleSelectAll(groupKeys)}
                          className="text-[10px] text-[hsl(var(--basic-500))] hover:underline cursor-pointer"
                        >
                          {allSelected ? 'Desmarcar' : 'Marcar'} todos
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-2 pl-6">
                      {group.permissions.map((perm) => (
                        <label 
                          key={perm.key} 
                          className={`flex items-center gap-3 cursor-pointer group ${isAdmin ? 'opacity-60' : ''}`}
                        >
                          <Checkbox
                            checked={permissions[perm.key as PermissionKey] || isAdmin}
                            onCheckedChange={(checked) => 
                              handlePermissionChange(perm.key as PermissionKey, !!checked)
                            }
                            disabled={isAdmin}
                            className="data-[state=checked]:bg-[hsl(var(--accent-500))] data-[state=checked]:border-[hsl(var(--accent-500))]"
                          />
                          <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                            {perm.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))] text-white"
            >
              {isSaving 
                ? (isNewUser ? 'Enviando...' : 'Salvando...') 
                : (isNewUser ? 'Enviar Convite' : 'Salvar')
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
